import * as React from 'react';
import { Combobox } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import { Place } from '@th/shared/src/types/Place';
import { PropertySnapshot } from '@th/shared/src/types/PropertySnapshot';
import ListingResult from './ListingResult';
import PropertySearchComboboxOption from './PropertySearchComboboxOption';

const LOCATION_SUGGESTIONS_LIMIT = 4;
const PROPERTY_SUGGESTIONS_LIMIT = 12;

type Props = {
  searchPhrase: string;
  places: Array<Place>;
  properties: Array<PropertySnapshot>;
  isSuggestionsFetching: boolean;
};

export default function PropertySearchResults({
  searchPhrase,
  places,
  properties,
  isSuggestionsFetching,
}: Props) {
  const hasSearchResults = [...places, ...properties].length > 0;
  const hasNoResultsAfterSearch = !hasSearchResults && !isSuggestionsFetching;
  const hasVeryFewSearchResults = [...places, ...properties].length <= 2;
  const showAiSearchSuggestions =
    searchPhrase.length > 5 &&
    (hasNoResultsAfterSearch || hasVeryFewSearchResults);
  const showPlaceAndListingSuggestions =
    searchPhrase.length > 3 && hasSearchResults;
  const showAnySearchSuggestions =
    showAiSearchSuggestions || showPlaceAndListingSuggestions;

  return (
    <div>
      {searchPhrase.length > 3 &&
        searchPhrase.length < 5 &&
        !hasSearchResults &&
        !isSuggestionsFetching && (
          <div
            className="absolute scroll-pt-11 scroll-pb-2 space-y-2 overflow-y-auto mt-2 pb-2 bg-background rounded-lg left-1/2 -translate-x-1/2 w-full lg:w-[60rem] border-2 px-4 py-2"
            style={{ maxHeight: '70vh' }}
          >
            <p>We can‘t find anything for that search. Try typing more...</p>
          </div>
        )}

      {showAnySearchSuggestions && (
        <Combobox.Options
          className="absolute scroll-pt-11 scroll-pb-2 space-y-2 overflow-y-auto mt-2 pb-2 bg-background rounded-lg left-1/2 -translate-x-1/2 w-full lg:w-[60rem] border-2 "
          style={{ maxHeight: '70vh' }}
        >
          {showAiSearchSuggestions && (
            <PropertySearchComboboxOption
              key={`nlp-${searchPhrase}`}
              item={searchPhrase}
            />
          )}

          {showPlaceAndListingSuggestions && (
            <>
              <li key={'locations'}>
                <h2 className="bg-gray-100 py-2.5 px-4 text-xs font-semibold text-gray-900">
                  Locations on Map
                </h2>
                <ul className="mt-2 text-sm text-gray-800">
                  {places.slice(0, LOCATION_SUGGESTIONS_LIMIT).map(place => (
                    <PropertySearchComboboxOption
                      key={`place-${place.name}`}
                      item={place}
                    />
                  ))}
                </ul>
              </li>
              <li key={'listings'}>
                <h2 className="bg-gray-100 py-2.5 px-4 text-xs font-semibold text-gray-900">
                  Listings
                </h2>
                <ul className="mt-2 text-sm text-gray-800">
                  {properties
                    .slice(0, PROPERTY_SUGGESTIONS_LIMIT)
                    .map(property => (
                      <Combobox.Option
                        key={`property-${property.listing.id}`}
                        value={property}
                        className={({ active }) =>
                          twMerge(
                            'cursor-default select-none px-4 py-2',
                            active && 'cursor-pointer bg-lightGray',
                          )
                        }
                      >
                        <ListingResult listing={property.listing} />
                      </Combobox.Option>
                    ))}
                </ul>
              </li>
            </>
          )}
        </Combobox.Options>
      )}
    </div>
  );
}
